import { useEffect, useState } from 'react'
import { api } from '../services/api'
import { QuizResult, Material } from '../types'
import LoadingSpinner from '../components/LoadingSpinner'
import { BarChart3, TrendingUp, Award, Clock, Trophy, Flame } from 'lucide-react'
import {
  Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, AreaChart,
} from 'recharts'
import { useAuthStore } from '../stores/authStore'
import { useUpgradeModal } from '../stores/upgradeModalStore'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-CR', { day: 'numeric', month: 'short' })
}

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${s < 10 ? '0' : ''}${s}s`
}

function scoreColor(pct: number) {
  if (pct >= 80) return '#10B981'
  if (pct >= 50) return '#FBBF24'
  return '#EF4444'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  const material = payload[0]?.payload?.material
  return (
    <div
      className="rounded-xl px-3 py-2 text-xs"
      style={{ background: '#1E293B', border: '1px solid rgba(255,255,255,0.1)', color: '#E2E8F0', maxWidth: 200 }}
    >
      {material && <p className="font-medium mb-0.5 truncate" style={{ color: '#A5B4FC' }}>{material}</p>}
      <p style={{ color: '#64748B' }} className="mb-1">{label}</p>
      <p className="font-bold" style={{ color: scoreColor(payload[0].value) }}>
        {payload[0].value}%
      </p>
    </div>
  )
}

export default function ResultsPage() {
  const { user } = useAuthStore()
  const { open: openUpgradeModal } = useUpgradeModal()
  const isPro = user?.plan === 'pro'

  const [results, setResults] = useState<QuizResult[]>([])
  const [materialsMap, setMaterialsMap] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      api.getUserResults() as Promise<{ results: QuizResult[] }>,
      api.getMaterials() as Promise<{ materials: Material[] }>,
    ])
      .then(([resultsData, matsData]) => {
        setResults(resultsData.results || [])
        const map: Record<string, string> = {}
        for (const m of matsData.materials || []) map[m.materialId] = m.title
        setMaterialsMap(map)
      })
      .catch(console.error)
      .finally(() => setIsLoading(false))
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Cargando resultados..." />
      </div>
    )
  }

  const percentages = results.map((r) => r.percentage ?? r.score ?? 0)
  const totalQuizzes = results.length
  const avgScore = totalQuizzes > 0 ? Math.round(percentages.reduce((a, b) => a + b, 0) / totalQuizzes) : 0
  const bestScore = totalQuizzes > 0 ? Math.round(Math.max(...percentages)) : 0
  const recentScore = totalQuizzes > 0 ? Math.round(percentages[0]) : 0

  // Streak: consecutive quizzes with score >= 60
  let streak = 0
  for (const r of results) {
    if ((r.percentage ?? r.score ?? 0) >= 60) streak++
    else break
  }

  // Chart data (last 10, chronological)
  const chartData = [...results]
    .slice(0, 10)
    .reverse()
    .map((r, i) => ({
      name: r.completedAt ? formatDate(r.completedAt) : `#${i + 1}`,
      score: Math.round(r.percentage ?? r.score ?? 0),
      material: materialsMap[r.materialId] ?? undefined,
    }))

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>Mis Resultados</h1>
        <p className="text-sm mt-1" style={{ color: '#64748B' }}>
          Tu progreso de aprendizaje
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total quizzes', value: totalQuizzes, icon: BarChart3, color: '#818CF8' },
          { label: 'Promedio',      value: `${avgScore}%`,   icon: TrendingUp, color: '#10B981' },
          { label: 'Mejor puntaje', value: `${bestScore}%`,  icon: Award,      color: '#FBBF24' },
          { label: 'Último quiz',   value: `${recentScore}%`, icon: Clock,     color: '#A78BFA' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="glass-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: '#475569' }}>
                  {label}
                </p>
                <p className="text-3xl font-bold" style={{ color: '#F1F5F9' }}>{value || '—'}</p>
              </div>
              <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color, opacity: 0.6 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Streak + Trophy */}
      {streak > 0 && (
        <div
          className="glass-card p-4 flex items-center gap-3"
          style={{ border: '1px solid rgba(251,191,36,0.2)' }}
        >
          <Flame className="w-5 h-5 flex-shrink-0" style={{ color: '#FBBF24' }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#FBBF24' }}>
              ¡Racha de {streak} quiz{streak > 1 ? 'zes' : ''} aprobados!
            </p>
            <p className="text-xs" style={{ color: '#64748B' }}>
              Mantén el ritmo para subir tu promedio
            </p>
          </div>
        </div>
      )}

      {/* Progress chart */}
      {chartData.length > 1 ? (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Progreso en el tiempo</h2>
            {!isPro && (
              <button
                onClick={() => openUpgradeModal('progress_chart')}
                className="text-xs px-3 py-1.5 rounded-lg transition-colors"
                style={{ color: '#818CF8', background: 'rgba(79,70,229,0.1)', border: '1px solid rgba(79,70,229,0.2)' }}
              >
                Ver análisis Pro
              </button>
            )}
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="name"
                tick={{ fill: '#475569', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fill: '#475569', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="score"
                stroke="#4F46E5"
                strokeWidth={2}
                fill="url(#scoreGrad)"
                dot={{ fill: '#4F46E5', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#818CF8' }}
              />
              {/* 80% reference line */}
              <Line
                type="monotone"
                dataKey={() => 80}
                stroke="#10B98130"
                strokeWidth={1}
                strokeDasharray="4 4"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      {/* Results list */}
      <div className="glass-card overflow-hidden">
        <div className="px-6 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Historial de quizzes</h2>
        </div>

        {results.length === 0 ? (
          <div className="p-10 text-center">
            <Trophy className="w-10 h-10 mx-auto mb-3" style={{ color: '#334155' }} />
            <p className="text-sm" style={{ color: '#64748B' }}>
              Aún no has completado ningún quiz
            </p>
            <p className="text-xs mt-1" style={{ color: '#475569' }}>
              Ve a Estudiar y completa tu primer quiz
            </p>
          </div>
        ) : (
          results.map((result, i) => {
            const pct = Math.round(result.percentage ?? result.score ?? 0)
            const color = scoreColor(pct)
            return (
              <div
                key={result.resultId || i}
                className="flex items-center gap-4 px-6 py-4 transition-colors"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
              >
                {/* Score badge */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold"
                  style={{ background: `${color}15`, color }}
                >
                  {pct}%
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-sm font-medium truncate" style={{ color: '#E2E8F0' }}>
                      {materialsMap[result.materialId]
                        ? <span className="truncate">{materialsMap[result.materialId]}</span>
                        : `Quiz #${results.length - i}`}
                    </p>
                    <span className="text-xs flex-shrink-0 ml-2" style={{ color: '#64748B' }}>
                      {result.completedAt ? formatDate(result.completedAt) : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    {materialsMap[result.materialId] && (
                      <span className="text-xs" style={{ color: '#475569' }}>Quiz #{results.length - i}</span>
                    )}
                    {(['easy','medium','hard'] as const).includes(result.difficulty as any) && (() => {
                      const dc = result.difficulty === 'hard' ? '#EF4444' : result.difficulty === 'easy' ? '#10B981' : '#FBBF24'
                      const dl = result.difficulty === 'hard' ? 'Difícil' : result.difficulty === 'easy' ? 'Fácil' : 'Medio'
                      return (
                        <span className="text-xs px-1.5 py-0.5 rounded-md font-medium"
                              style={{ background: `${dc}18`, color: dc, border: `1px solid ${dc}30` }}>
                          {dl}
                        </span>
                      )
                    })()}
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div
                      className="h-1.5 rounded-full transition-all"
                      style={{ width: `${pct}%`, background: color }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs" style={{ color: '#475569' }}>
                      {result.score ?? '?'} / {result.total ?? '?'} correctas
                    </span>
                    {result.timeSpent > 0 && (
                      <span className="text-xs" style={{ color: '#475569' }}>
                        {formatTime(result.timeSpent)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
